/**
 * Live-slate countdown — header widget.
 *
 * Sprint 60.111 rework (Aug 2026):
 *   * Chips are now LIVE loaded DK slates from `/api/dk/slates`, not the
 *     4 hardcoded Thu/Sun/Sun-Late/Mon windows. Each chip shows the actual
 *     slate label (e.g. "Main · 12G", "Showdown · NE@SEA").
 *   * Clicking a chip APPLIES that slate (same as picking from Slate
 *     Selection → dropdown). Also drives strategy-preset inference:
 *     main → milly_maker, showdown → small_gpp, all others → three_max.
 *   * The countdown re-anchors to the SELECTED slate's `earliest_kickoff`
 *     — DK locks each slate at the first game's kickoff. Offseason (no
 *     slates loaded / all past) falls back to the NFL season opener.
 *   * All time displays use the browser's local timezone via
 *     `Intl.DateTimeFormat().resolvedOptions().timeZone`. No IP geo lookup
 *     needed — the browser already knows the user's tz from the OS.
 *
 * Preserved from Sprint 31:
 *   * Threshold-based push notifications (60/15/5 min defaults, editable)
 *   * Auto Attack Plan + Auto Consensus Advisor (armed N min before lock)
 */
import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { announceSlateApply } from "@/components/SlateBleedGuard";
import { toast } from "sonner";
import { Bell, BellSlash, Clock, GearSix, Robot } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const PRESET_LABEL = {
  milly_maker: "Milly Maker · 150-max GPP",
  small_gpp:   "Small GPP · <5k field",
  three_max:   "3-Max GPP",
  cash_50_50:  "Cash · 50/50 / Double-up",
  "20_max":    "20-Max Play Action GPP",
  single_entry_150: "Single-Entry · 150 slots",
  flea_flicker: "Flea Flicker",
  big_play_25:  "Big Play $25",
};

// NFL 2026 season opener — used as offseason fallback anchor.
const NFL_SEASON_OPENER_ISO = "2026-09-09T20:20:00-04:00";
const NFL_SEASON_END_ISO    = "2027-01-10T23:59:00-05:00";

function isRegularSeasonActive(now = new Date()) {
  return now >= new Date(NFL_SEASON_OPENER_ISO) && now <= new Date(NFL_SEASON_END_ISO);
}

// Broadcast preset change so Optimizer/WarRoom/etc. can react.
function broadcastPreset(preset, source = "kickoff") {
  try { localStorage.setItem("wp_active_preset", preset); } catch (_) { /* private-mode safe */ }
  try {
    window.dispatchEvent(new CustomEvent("wp:preset-change", { detail: { preset, source } }));
  } catch (_) { /* CustomEvent unsupported */ }
}

const DEFAULT_THRESHOLDS = [60, 15, 5];
const STORE_KEY = "wp_kickoff_prefs_v2"; // v2: renamed selected key, keep prefs separate
const AUTO_PLAN_LEAD_OPTIONS = [30, 60, 90, 120];
const DEFAULT_AUTO_PLAN_LEAD = 90;
const SLATE_POLL_MS = 120_000; // re-poll loaded slates every 2 min

// Slate → default preset. Main-slate 12+ games = ceiling posture; single-game
// showdown = small-field chalk-eating; anything in between = mid.
function presetForSlate(s) {
  if (!s) return "milly_maker";
  if (s.is_showdown) return "small_gpp";
  const games = Number(s.distinct_games || 0);
  if (games >= 10) return "milly_maker";
  if (games <= 4)  return "small_gpp";
  return "three_max";
}

function modeForSlate(s) {
  if (!s) return "large";
  if (s.is_showdown) return "small";
  const games = Number(s.distinct_games || 0);
  if (games >= 10) return "large";
  if (games <= 4)  return "small";
  return "mid";
}

const MODE_DOT = { small: "#39FF14", mid: "#FFCC00", large: "#FF3B30" };

// Short chip label — trims the full backend label down to fit the header.
// "Main Slate · 12 games · 1:00 PM ET" → "Main · 12G"
// "Showdown · NE@SEA · Wed 8:20 PM ET" → "SD · NE@SEA"
function shortLabel(s) {
  if (!s) return "";
  if (s.is_showdown) {
    // Extract the matchup token (looks like "NE@SEA") from the label.
    const m = /([A-Z]{2,3}@[A-Z]{2,3})/.exec(s.label || "");
    return `SD · ${m ? m[1] : "single-game"}`;
  }
  const games = Number(s.distinct_games || 0);
  const base = (s.slate_type || "main").toString();
  // Capitalize type: main→Main, tnf→TNF, showdown-late→Showdown Late
  const nice = base.length <= 3 ? base.toUpperCase() : base.charAt(0).toUpperCase() + base.slice(1);
  return `${nice}${games ? ` · ${games}G` : ""}`;
}

function fmtRemaining(ms) {
  if (ms <= 0) return "LOCKED";
  const totalSec = Math.floor(ms / 1000);
  const d = Math.floor(totalSec / 86400);
  const h = Math.floor((totalSec % 86400) / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const pad = (n) => n.toString().padStart(2, "0");
  if (d > 0) return `${d}d ${pad(h)}:${pad(m)}:${pad(s)}`;
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

// Browser's local tz — used for display. Falls back to America/New_York.
const USER_TZ =
  (typeof Intl !== "undefined" && Intl.DateTimeFormat().resolvedOptions().timeZone)
  || "America/New_York";

function fmtLockLocal(d) {
  return d.toLocaleString(undefined, {
    weekday: "short", month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit",
    timeZone: USER_TZ,
  });
}

function loadPrefs() {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}

function savePrefs(prefs) {
  try { localStorage.setItem(STORE_KEY, JSON.stringify(prefs)); }
  catch (_) { /* private-mode / quota-exceeded safe */ }
}

export default function KickoffCountdown() {
  const stored = loadPrefs();
  const [slates, setSlates] = useState([]);
  const [activeDg, setActiveDg] = useState(stored?.selectedDg || null);
  const [applyingDg, setApplyingDg] = useState(null);
  const [notifyOn, setNotifyOn] = useState(stored?.notifyOn ?? false);
  const [thresholds, setThresholds] = useState(stored?.thresholds || DEFAULT_THRESHOLDS);
  const [autoPlanOn, setAutoPlanOn] = useState(stored?.autoPlanOn ?? false);
  const [autoPlanLead, setAutoPlanLead] = useState(stored?.autoPlanLead ?? DEFAULT_AUTO_PLAN_LEAD);
  const [autoAdvisorOn, setAutoAdvisorOn] = useState(stored?.autoAdvisorOn ?? true);
  const [permission, setPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "denied"
  );
  const [now, setNow] = useState(new Date());
  const [showSettings, setShowSettings] = useState(false);
  const firedRef = useRef({});
  const autoPlanFiredRef = useRef({});
  const autoPlanBusyRef = useRef(false);
  const autoAdvisorFiredRef = useRef({});
  const autoAdvisorBusyRef = useRef(false);
  const rootRef = useRef(null);

  // ---------- Slate feed ----------

  // Auto-heal: the backend state stays pinned to whatever slate was last
  // applied. Once those games kick off (or DK prunes the draftgroup) the
  // app keeps showing a dead slate — "DEN@KC · MON 8:15 PM" with VEGAS 0/1.
  // When we detect that, apply the next upcoming DK slate once per session.
  const healedRef = useRef(false);
  const autoHealStaleSlate = useCallback(async (list) => {
    if (healedRef.current || list.length === 0) return;
    const upcoming = list.filter(s => new Date(s.earliest_kickoff || 0) > new Date());
    if (upcoming.length === 0) return;
    let info;
    try { info = await api.slateInfo(); } catch { return; }
    const pinned = info?.active_slate_dg || null;
    const pinnedSlate = pinned ? list.find(s => s.dg === pinned) : null;
    const pinnedLive = pinnedSlate && new Date(pinnedSlate.earliest_kickoff || 0) > new Date();
    if (pinnedLive) return;
    healedRef.current = true;
    const next = upcoming[0];
    try {
      const r = await api.dkSlateApply(next.dg);
      announceSlateApply(r);
      setActiveDg(next.dg);
      broadcastPreset(presetForSlate(next), "kickoff-autoheal");
      toast.success(`Slate auto-updated · ${r.label || next.label}`, {
        description: pinned
          ? "Your previous slate has locked, so the pool was switched to the next live slate."
          : "No live slate was loaded, so the next DK slate was applied.",
      });
      try { window.dispatchEvent(new CustomEvent("wp:slate-applied", { detail: { dg: next.dg } })); }
      catch { /* noop */ }
    } catch (e) {
      healedRef.current = false;
      console.debug("slate auto-heal failed:", e?.message);
    }
  }, []);

  const loadSlates = useCallback(async () => {
    try {
      const r = await api.dkSlatesList();
      const list = Array.isArray(r?.slates) ? r.slates : [];
      // Sort by earliest_kickoff ascending — the next slate to lock is first.
      list.sort((a, b) => {
        const ta = new Date(a.earliest_kickoff || 0).getTime();
        const tb = new Date(b.earliest_kickoff || 0).getTime();
        return ta - tb;
      });
      setSlates(list);
      // If active slate isn't in the list (e.g. rolled off), fall back to
      // the first upcoming one.
      setActiveDg(prev => {
        if (prev && list.some(s => s.dg === prev)) return prev;
        const upcoming = list.find(s => new Date(s.earliest_kickoff || 0) > new Date());
        return (upcoming || list[0])?.dg || null;
      });
      autoHealStaleSlate(list);
    } catch (e) {
      // Non-fatal — chip strip will collapse to the offseason clock.
      console.debug("kickoff slate load failed:", e?.message);
    }
  }, [autoHealStaleSlate]);

  useEffect(() => {
    loadSlates();
    const t = setInterval(loadSlates, SLATE_POLL_MS);
    return () => clearInterval(t);
  }, [loadSlates]);

  // ---------- Outside-click dismiss for settings ----------
  useEffect(() => {
    if (!showSettings) return;
    const onClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setShowSettings(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [showSettings]);

  // ---------- Chip click: apply slate + broadcast preset ----------
  const handleChipClick = async (slate) => {
    if (!slate || slate.dg === activeDg || applyingDg) return;
    const preset = presetForSlate(slate);
    setActiveDg(slate.dg);
    setApplyingDg(slate.dg);
    broadcastPreset(preset, "kickoff-tab");
    toast.success(`Applying ${slate.label || shortLabel(slate)}…`, {
      description: `Strategy → ${PRESET_LABEL[preset] || preset}`,
    });
    try {
      const r = await api.dkSlateApply(slate.dg);
      announceSlateApply(r);
      toast.success(`Loaded ${r.label || slate.label} · ${r.player_count} players · ${r.n_games} games`);
      // Nudge other listeners (Slate Selection, top-nav pill) to refresh.
      try { window.dispatchEvent(new CustomEvent("wp:slate-applied", { detail: { dg: slate.dg } })); }
      catch { /* noop */ }
    } catch (e) {
      toast.error(`Slate apply failed: ${e?.message || "server error"}`);
      // Revert selection on failure so the UI doesn't lie about active slate.
      setActiveDg(prev => prev === slate.dg ? null : prev);
    } finally {
      setApplyingDg(null);
    }
  };

  // ---------- Seed preset on mount if none stored ----------
  useEffect(() => {
    try {
      if (!localStorage.getItem("wp_active_preset") && slates.length > 0) {
        const s = slates.find(x => x.dg === activeDg) || slates[0];
        if (s) broadcastPreset(presetForSlate(s), "kickoff-init");
      }
    } catch (_) { /* localStorage disabled */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slates.length]);

  // ---------- React to external preset changes (Optimizer picker etc) ----------
  useEffect(() => {
    const onPresetChange = (e) => {
      const src = e?.detail?.source;
      if (src === "kickoff-tab" || src === "kickoff-init") return;
      // We DON'T re-map back to a slate here — the active slate is
      // determined by the user's explicit slate click, not by a preset
      // change from a different page.
    };
    window.addEventListener("wp:preset-change", onPresetChange);
    return () => window.removeEventListener("wp:preset-change", onPresetChange);
  }, []);

  // ---------- Persist prefs ----------
  useEffect(() => {
    savePrefs({
      selectedDg: activeDg,
      notifyOn, thresholds, autoPlanOn, autoPlanLead, autoAdvisorOn,
    });
  }, [activeDg, notifyOn, thresholds, autoPlanOn, autoPlanLead, autoAdvisorOn]);

  // ---------- 1Hz tick ----------
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // ---------- Derived: current slate + countdown target ----------
  const current = useMemo(() => {
    const s = slates.find(x => x.dg === activeDg) || slates[0] || null;
    const target = s?.earliest_kickoff
      ? new Date(s.earliest_kickoff)
      : new Date(NFL_SEASON_OPENER_ISO); // fallback anchor
    return { slate: s, target, remainingMs: target.getTime() - now.getTime() };
  }, [slates, activeDg, now]);

  // ---------- Notifications ----------
  useEffect(() => {
    if (!notifyOn || permission !== "granted" || typeof Notification === "undefined") return;
    if (!current.slate) return;
    const minsLeft = current.remainingMs / 60000;
    for (const th of thresholds) {
      const key = `${current.slate.dg}_${current.target.getTime()}_${th}`;
      if (firedRef.current[key]) continue;
      if (minsLeft <= th && minsLeft > th - 0.02) {
        firedRef.current[key] = true;
        try {
          new Notification("WarPaint DFS — Slate Lock Warning", {
            body: `${shortLabel(current.slate)} locks in ${th} min (${fmtLockLocal(current.target)})`,
            tag: key,
            icon: "/brand/helmet_transparent.png",
          });
        } catch (e) { console.debug("Notification blocked:", e?.message); }
      }
    }
  }, [now, notifyOn, permission, thresholds, current]);

  // ---------- Auto Attack Plan ----------
  useEffect(() => {
    if (!autoPlanOn || !current.slate) return;
    if (autoPlanBusyRef.current) return;
    const minsLeft = current.remainingMs / 60000;
    const key = `${current.slate.dg}_${current.target.getTime()}`;
    if (autoPlanFiredRef.current[key]) return;
    if (minsLeft > autoPlanLead || minsLeft < autoPlanLead - 0.5) return;
    autoPlanFiredRef.current[key] = true;
    autoPlanBusyRef.current = true;
    (async () => {
      try {
        const preset = presetForSlate(current.slate);
        const plan = await api.aiAttackPlan(preset);
        if (permission === "granted" && typeof Notification !== "undefined") {
          try {
            new Notification("WarPaint DFS — Attack Plan Ready", {
              body: `${shortLabel(current.slate)} — brief drafted ${autoPlanLead} min before lock.`,
              tag: `attack-plan-${key}`,
              icon: "/brand/helmet_transparent.png",
              requireInteraction: true,
            });
          } catch (e) { console.debug("attack-plan Notification blocked:", e?.message); }
        }
        try {
          window.dispatchEvent(new CustomEvent("wp:attack-plan-auto", {
            detail: { plan, slate_dg: current.slate.dg, preset },
          }));
        } catch (e) { console.debug("attack-plan dispatch failed:", e?.message); }
      } catch (e) {
        autoPlanFiredRef.current[key] = false;
      } finally {
        autoPlanBusyRef.current = false;
      }
    })();
  }, [now, autoPlanOn, autoPlanLead, current, permission]);

  // ---------- Auto Consensus Advisor ----------
  useEffect(() => {
    if (!autoAdvisorOn || !current.slate) return;
    if (autoAdvisorBusyRef.current) return;
    const minsLeft = current.remainingMs / 60000;
    const key = `${current.slate.dg}_${current.target.getTime()}`;
    if (autoAdvisorFiredRef.current[key]) return;
    if (minsLeft > autoPlanLead || minsLeft < autoPlanLead - 0.5) return;
    autoAdvisorFiredRef.current[key] = true;
    autoAdvisorBusyRef.current = true;
    (async () => {
      try {
        const advisor = await api.aiConsensusAdvisor();
        try {
          window.dispatchEvent(new CustomEvent("wp:consensus-advisor-auto", {
            detail: { advisor, slate_dg: current.slate.dg },
          }));
        } catch (e) { console.debug("advisor dispatch failed:", e?.message); }
        if (permission === "granted" && typeof Notification !== "undefined") {
          try {
            const label = advisor?.synthetic ? "WarPaint-Native Chalk Alert" : "Consensus Advisor";
            new Notification(`WarPaint DFS — ${label} Ready`, {
              body: `${shortLabel(current.slate)} · ${advisor?.n_pivots || 0} sub-5% pivots.`,
              tag: `advisor-${key}`,
              icon: "/brand/helmet_transparent.png",
            });
          } catch (e) { console.debug("advisor Notification blocked:", e?.message); }
        }
      } catch (e) {
        autoAdvisorFiredRef.current[key] = false;
      } finally {
        autoAdvisorBusyRef.current = false;
      }
    })();
  }, [now, autoAdvisorOn, autoPlanLead, current, permission]);

  const requestPerm = async () => {
    if (typeof Notification === "undefined") return;
    try {
      const p = await Notification.requestPermission();
      setPermission(p);
      if (p === "granted") setNotifyOn(true);
    } catch (e) { console.debug("Notification.requestPermission failed:", e?.message); }
  };

  const toggleNotify = async () => {
    if (!notifyOn) {
      if (permission !== "granted") { await requestPerm(); return; }
      setNotifyOn(true);
    } else {
      setNotifyOn(false);
    }
  };

  const toggleThreshold = (m) => {
    const set = new Set(thresholds);
    if (set.has(m)) set.delete(m); else set.add(m);
    setThresholds(Array.from(set).sort((a, b) => b - a));
  };

  const isLocked = current.remainingMs <= 0;
  const noSlates = slates.length === 0;
  const isOffseason = noSlates && !isRegularSeasonActive();
  const currentShort = current.slate ? shortLabel(current.slate) : "NFL Week 1";

  return (
    <div
      ref={rootRef}
      data-testid="kickoff-countdown"
      className="relative flex items-center gap-2 border border-zinc-800 bg-zinc-950 px-3 py-1.5 flex-shrink-0"
    >
      {/* Live slate chips */}
      <div className="flex items-center gap-1">
        {noSlates && (
          <div className="text-[9px] mono tracking-widest text-zinc-600 px-2 py-1 border border-zinc-900">
            NO SLATES LOADED
          </div>
        )}
        {slates.map(s => {
          const active = s.dg === activeDg;
          const remaining = new Date(s.earliest_kickoff || 0).getTime() - now.getTime();
          const soon = remaining > 0 && remaining < 3600_000;
          const applying = applyingDg === s.dg;
          const dotColor = MODE_DOT[modeForSlate(s)] || "#71717a";
          return (
            <button
              key={s.dg}
              data-testid={`kickoff-slate-${s.dg}`}
              onClick={() => handleChipClick(s)}
              disabled={applying}
              className={`flex items-center gap-1.5 text-[9px] mono tracking-widest px-2 py-1 border transition-colors whitespace-nowrap flex-shrink-0 ${
                applying
                  ? "bg-zinc-800 text-zinc-500 border-zinc-700 cursor-wait"
                  : active
                    ? "bg-[#39FF14] text-black border-[#39FF14] font-bold"
                    : soon
                      ? "text-[#FFCC00] border-[#FFCC00]/40 hover:bg-zinc-900"
                      : "text-zinc-400 border-zinc-800 hover:text-white hover:bg-zinc-900"
              }`}
              title={`${s.label || shortLabel(s)} · locks ${fmtLockLocal(new Date(s.earliest_kickoff))}`}
            >
              <span
                className="inline-block w-1.5 h-1.5 rounded-full"
                style={{ background: active ? "#000" : dotColor }}
                aria-hidden
              />
              {shortLabel(s)}
            </button>
          );
        })}
      </div>

      {/* Countdown */}
      <div className="flex items-center gap-2 pl-2 border-l border-zinc-800">
        <Clock size={14} className={isLocked ? "text-[#FF3B30]" : "text-[#39FF14]"} weight="bold" />
        <div className="leading-none">
          <div
            data-testid="kickoff-countdown-value"
            className={`mono text-[13px] font-bold tracking-wider ${
              isLocked ? "text-[#FF3B30]" : (isOffseason ? "text-[#FFCC00]" : "text-white")
            }`}
          >
            {fmtRemaining(current.remainingMs)}
          </div>
          <div className="text-[8px] mono text-zinc-500 tracking-widest uppercase mt-0.5">
            {isOffseason
              ? "until NFL Week 1 kickoff"
              : current.slate
                ? `until ${currentShort} lock`
                : "no slate selected"}
          </div>
        </div>
      </div>

      {/* Offseason mode badge */}
      {isOffseason && (
        <div
          data-testid="offseason-badge"
          title="NFL 2026 kicks off Wed Sept 9, 2026 8:20 PM ET. Countdown targets the opener until slates start loading."
          className="px-2 py-1 border border-[#FFCC00]/40 text-[#FFCC00] flex items-center gap-1 bg-[#FFCC00]/5"
        >
          <span className="text-[9px] mono tracking-widest font-bold">OFFSEASON</span>
        </div>
      )}

      {/* Auto-fire armed indicator */}
      {(autoPlanOn || autoAdvisorOn) && current.slate && (
        <div
          data-testid="auto-plan-armed"
          title={`Armed ${autoPlanLead} min before ${currentShort} lock: ${[autoPlanOn && "Attack Plan", autoAdvisorOn && "Consensus Advisor"].filter(Boolean).join(" + ")}`}
          className={`p-1.5 border flex items-center gap-1 ${
            autoPlanOn && autoAdvisorOn
              ? "border-[#39FF14]/40 text-[#39FF14]"
              : autoPlanOn
                ? "border-[#39FF14]/40 text-[#39FF14]"
                : "border-[#FF8A00]/40 text-[#FF8A00]"
          }`}
        >
          <Robot size={12} weight="fill" />
          <span className="text-[9px] mono tracking-widest font-bold">{autoPlanLead}m</span>
        </div>
      )}

      {/* Notification toggle */}
      <button
        data-testid="kickoff-notify-toggle"
        onClick={toggleNotify}
        className={`p-1.5 border transition-colors ${
          notifyOn && permission === "granted"
            ? "text-[#39FF14] border-[#39FF14]"
            : "text-zinc-500 border-zinc-800 hover:text-white"
        }`}
        title={notifyOn ? "Notifications ON" : "Enable push notifications"}
      >
        {notifyOn && permission === "granted" ? <Bell size={12} weight="fill" /> : <BellSlash size={12} />}
      </button>

      {/* Settings */}
      <button
        data-testid="kickoff-settings-toggle"
        onClick={() => setShowSettings(v => !v)}
        className="p-1.5 text-zinc-500 hover:text-white border border-zinc-800 transition-colors"
        title="Notification settings"
      >
        <GearSix size={12} />
      </button>

      {showSettings && (
        <div
          data-testid="kickoff-settings-panel"
          className="absolute top-full right-0 mt-2 w-72 bg-zinc-950 border border-zinc-800 shadow-2xl p-4 z-50"
        >
          <div className="overline text-[10px] text-zinc-400 mb-3">Push Notification Settings</div>
          {permission !== "granted" && (
            <button
              onClick={requestPerm}
              className="w-full mb-3 px-3 py-2 bg-[#39FF14] text-black text-[10px] mono font-bold tracking-widest hover:bg-[#39FF14]/80"
              data-testid="kickoff-request-permission"
            >
              GRANT BROWSER PERMISSION
            </button>
          )}
          <div className="text-[10px] mono text-zinc-500 mb-2 tracking-widest uppercase">
            Ping me before lock
          </div>
          <div className="space-y-1.5">
            {[120, 60, 30, 15, 10, 5, 1].map(m => {
              const on = thresholds.includes(m);
              return (
                <label
                  key={m}
                  data-testid={`kickoff-threshold-${m}`}
                  className={`flex items-center justify-between px-3 py-1.5 border cursor-pointer transition-colors ${
                    on ? "border-[#39FF14] bg-[#39FF14]/5" : "border-zinc-800 hover:border-zinc-700"
                  }`}
                >
                  <span className={`text-[11px] mono ${on ? "text-[#39FF14]" : "text-zinc-400"}`}>
                    {m} min
                  </span>
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={() => toggleThreshold(m)}
                    className="accent-[#39FF14]"
                  />
                </label>
              );
            })}
          </div>
          <div className="text-[9px] mono text-zinc-600 mt-3 leading-relaxed">
            Alerts fire for the currently selected slate ({currentShort}). Times shown in your local zone ({USER_TZ.replace(/^.*\//, "")}).
          </div>

          {/* Auto Attack Plan */}
          <div className="mt-4 pt-4 border-t border-zinc-800">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Robot size={14} className={autoPlanOn ? "text-[#39FF14]" : "text-zinc-500"} weight={autoPlanOn ? "fill" : "regular"} />
                <span className="overline text-[10px]">Auto Attack Plan</span>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  data-testid="auto-plan-toggle"
                  type="checkbox"
                  checked={autoPlanOn}
                  onChange={() => setAutoPlanOn(v => !v)}
                  className="sr-only peer"
                />
                <div className="w-9 h-5 bg-zinc-800 peer-checked:bg-[#39FF14] rounded-full peer transition-colors relative pointer-events-none">
                  <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${autoPlanOn ? "translate-x-4" : ""}`} />
                </div>
              </label>
            </div>
            <div className="text-[9px] mono text-zinc-600 leading-relaxed">
              Auto-drafts a Claude Sonnet 5 slate brief {autoPlanLead} min before {currentShort} lock. Saved to Slate Intel + push notification.
            </div>
          </div>

          {/* Auto Consensus Advisor */}
          <div className="mt-4 pt-4 border-t border-zinc-800">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Robot size={14} className={autoAdvisorOn ? "text-[#FF8A00]" : "text-zinc-500"} weight={autoAdvisorOn ? "fill" : "regular"} />
                <span className="overline text-[10px]">Auto Consensus Advisor</span>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  data-testid="auto-advisor-toggle"
                  type="checkbox"
                  checked={autoAdvisorOn}
                  onChange={() => setAutoAdvisorOn(v => !v)}
                  className="sr-only peer"
                />
                <div className="w-9 h-5 bg-zinc-800 peer-checked:bg-[#FF8A00] rounded-full peer transition-colors relative pointer-events-none">
                  <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${autoAdvisorOn ? "translate-x-4" : ""}`} />
                </div>
              </label>
            </div>
            <div className="text-[9px] mono text-zinc-600 leading-relaxed">
              Refreshes Consensus Advisor {autoPlanLead} min before {currentShort} lock. Falls back to WarPaint-Native when no competitor CSVs are uploaded.
            </div>
          </div>

          {/* Lead-time picker */}
          <div className="mt-4 pt-4 border-t border-zinc-800">
            <div className="text-[10px] mono text-zinc-500 mb-2 tracking-widest uppercase">
              Lead time · minutes before lock
            </div>
            <div className="grid grid-cols-4 gap-1.5">
              {AUTO_PLAN_LEAD_OPTIONS.map(m => {
                const on = autoPlanLead === m;
                const anyAutoOn = autoPlanOn || autoAdvisorOn;
                return (
                  <button
                    key={m}
                    data-testid={`auto-plan-lead-${m}`}
                    onClick={() => setAutoPlanLead(m)}
                    disabled={!anyAutoOn}
                    className={`px-2 py-1.5 text-[10px] mono border transition-colors ${
                      on && anyAutoOn
                        ? "border-[#39FF14] bg-[#39FF14]/10 text-[#39FF14] font-bold"
                        : anyAutoOn
                          ? "border-zinc-800 text-zinc-400 hover:border-zinc-700"
                          : "border-zinc-900 text-zinc-700 cursor-not-allowed"
                    }`}
                  >
                    {m}m
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
